import { useState, useEffect } from 'react';
import { api } from '../../api';
import type { DomainDetail } from '../../types';

// ── Types ────────────────────────────────────────────────────────────────────

type InstallBranch = 'wp' | 'non-wp';
type WizardStep =
  | 'add-domain'
  | 'detect-platform'
  | 'install-wp'
  | 'install-non-wp'
  | 'verify'
  | 'done';

interface Props {
  /** Called when the user has a domain and is ready to proceed to full onboarding. */
  onProceed: (domainId: string) => void;
}

// ── Helper ───────────────────────────────────────────────────────────────────

function normaliseDomain(raw: string): string {
  return raw.trim().toLowerCase()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./i, '');
}

// ── Step components ───────────────────────────────────────────────────────────

function AddDomainStep({
  onNext,
}: {
  onNext: (domain: DomainDetail) => void;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const normalised = normaliseDomain(value);
    if (!normalised) { setError('Please enter a domain.'); return; }
    setLoading(true);
    try {
      const domain = await api.domains.create(normalised);
      onNext(domain);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add domain.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-brand-ink">Add your domain</h2>
      <p className="text-sm text-gray-600">Enter the domain you want FixMySEO to optimise for AI visibility.</p>
      <form onSubmit={submit} className="space-y-3">
        <input
          type="text"
          placeholder="e.g. wigglewifi.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-brand-green-dark text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green disabled:opacity-50"
        >
          {loading ? 'Adding…' : 'Continue →'}
        </button>
      </form>
    </div>
  );
}

function DetectPlatformStep({
  domain,
  onBranch,
}: {
  domain: DomainDetail;
  onBranch: (branch: InstallBranch) => void;
}) {
  const [detecting, setDetecting] = useState(true);
  const [detected, setDetected] = useState<boolean | null>(null);
  const [confidence, setConfidence] = useState<'high' | 'medium' | 'low'>('low');
  const [override, setOverride] = useState<InstallBranch | null>(null);

  useEffect(() => {
    api.domains.detectPlatform(domain.domain)
      .then((r) => {
        setDetected(r.isWordPress);
        setConfidence(r.confidence);
      })
      .catch(() => setDetected(null))
      .finally(() => setDetecting(false));
  }, [domain.domain]);

  const effectiveBranch: InstallBranch = override ?? (detected ? 'wp' : 'non-wp');

  if (detecting) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-brand-ink">Detecting your platform…</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
          Probing {domain.domain}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-brand-ink">How would you like to install?</h2>

      {detected !== null && (
        <div className={`rounded-lg px-4 py-3 text-sm ${detected ? 'bg-green-50 text-brand-green' : 'bg-gray-50 text-gray-700'}`}>
          {detected
            ? `WordPress detected${confidence === 'high' ? ' (confirmed)' : ' (likely)'} on ${domain.domain}.`
            : `No WordPress detected on ${domain.domain}.`}
        </div>
      )}

      {/* Branch selector */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setOverride('wp')}
          className={`rounded-xl border-2 p-4 text-left transition-all ${
            effectiveBranch === 'wp'
              ? 'border-brand-green bg-green-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="font-semibold text-sm text-brand-ink mb-1">WordPress plugin</div>
          <div className="text-xs text-gray-500">Install one plugin — does everything.</div>
        </button>
        <button
          onClick={() => setOverride('non-wp')}
          className={`rounded-xl border-2 p-4 text-left transition-all ${
            effectiveBranch === 'non-wp'
              ? 'border-brand-green bg-green-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="font-semibold text-sm text-brand-ink mb-1">One line of code</div>
          <div className="text-xs text-gray-500">Paste one script tag — works everywhere.</div>
        </button>
      </div>

      <button
        onClick={() => onBranch(effectiveBranch)}
        className="w-full bg-brand-green-dark text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green"
      >
        Continue with {effectiveBranch === 'wp' ? 'WordPress plugin' : 'one-line tag'} →
      </button>
    </div>
  );
}

function WpInstallStep({
  domain,
  onVerified,
}: {
  domain: DomainDetail;
  onVerified: () => void;
}) {
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState<'idle' | 'not-detected' | 'detected'>('idle');

  async function checkPresence() {
    setChecking(true);
    setStatus('idle');
    try {
      const d = await api.domains.get(domain.id);
      if (d.lastBeaconAt) {
        setStatus('detected');
        setTimeout(onVerified, 1000);
      } else {
        setStatus('not-detected');
      }
    } catch {
      setStatus('not-detected');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-brand-ink">Install the FixMySEO WordPress plugin</h2>
      <p className="text-sm text-gray-600">
        One plugin — core SEO fixes <strong>and</strong> AI Truth serving. Download, install, and connect to your account.
      </p>

      <div className="rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-green text-white text-xs flex items-center justify-center font-bold">1</span>
          <div>
            <div className="text-sm font-medium text-brand-ink">Download the plugin</div>
            <a
              href="https://api.fixmyseo.io/cdn/plugins/fixmyseo.zip"
              className="text-xs text-brand-green-dark underline"
              download
            >
              fixmyseo.zip
            </a>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-green text-white text-xs flex items-center justify-center font-bold">2</span>
          <div className="text-sm font-medium text-brand-ink">
            Upload to WordPress: Plugins → Add New → Upload Plugin
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-green text-white text-xs flex items-center justify-center font-bold">3</span>
          <div className="text-sm font-medium text-brand-ink">
            Activate and connect to your FixMySEO account from the plugin settings
          </div>
        </div>
      </div>

      {status === 'detected' && (
        <p className="text-sm text-brand-green bg-green-50 rounded-lg px-4 py-3">
          Plugin detected — you're connected!
        </p>
      )}
      {status === 'not-detected' && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-4 py-3">
          Plugin not yet detected. Complete the install steps above, then check again.
        </p>
      )}

      <button
        onClick={checkPresence}
        disabled={checking || status === 'detected'}
        className="w-full bg-brand-green-dark text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green disabled:opacity-50"
      >
        {checking ? 'Checking…' : status === 'detected' ? 'Connected!' : 'I\'ve installed the plugin — verify'}
      </button>
    </div>
  );
}

function NonWpInstallStep({
  domain,
  onVerified,
}: {
  domain: DomainDetail;
  onVerified: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState<'idle' | 'not-detected' | 'detected'>('idle');

  const snippet = `<script src="https://api.fixmyseo.io/cdn/tag/${domain.id}.js" async></script>`;

  function copy() {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function checkBeacon() {
    setChecking(true);
    setStatus('idle');
    try {
      const d = await api.domains.get(domain.id);
      if (d.lastBeaconAt) {
        setStatus('detected');
        // Auto-provision AI Truth subdomain behind the scenes
        try {
          await api.aiTruth.activate(domain.id);
        } catch {
          // Non-fatal — subdomain might already exist or flag might be off
        }
        setTimeout(onVerified, 1000);
      } else {
        setStatus('not-detected');
      }
    } catch {
      setStatus('not-detected');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-brand-ink">Add one line to your site</h2>
      <p className="text-sm text-gray-600">
        Paste this into the <code className="text-xs bg-gray-100 px-1 rounded">&lt;head&gt;</code> of every page. That's it — core SEO fixes and AI Truth both activate automatically.
      </p>

      <div className="bg-gray-900 rounded-xl p-4 relative">
        <code className="text-green-400 text-xs break-all">{snippet}</code>
        <button
          onClick={copy}
          className="absolute top-3 right-3 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded px-2 py-1"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      <div className="text-xs text-gray-500 space-y-1">
        <p>Not a developer? <button className="underline text-brand-green-dark">Email the snippet to your developer</button></p>
      </div>

      {status === 'detected' && (
        <p className="text-sm text-brand-green bg-green-50 rounded-lg px-4 py-3">
          Tag detected — you're connected!
        </p>
      )}
      {status === 'not-detected' && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-4 py-3">
          Tag not yet detected. Paste the snippet, deploy your site, then check again.
        </p>
      )}

      <button
        onClick={checkBeacon}
        disabled={checking || status === 'detected'}
        className="w-full bg-brand-green-dark text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green disabled:opacity-50"
      >
        {checking ? 'Checking…' : status === 'detected' ? 'Connected!' : 'I\'ve added the tag — verify'}
      </button>
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────

export function UnifiedOnboardingWizard({ onProceed }: Props) {
  const [step, setStep] = useState<WizardStep>('add-domain');
  const [domain, setDomain] = useState<DomainDetail | null>(null);
  const [branch, setBranch] = useState<InstallBranch | null>(null);

  function handleDomainCreated(d: DomainDetail) {
    setDomain(d);
    // If the domain already has a beacon (re-onboarding), skip to done
    if (d.lastBeaconAt) {
      setStep('done');
      onProceed(d.id);
      return;
    }
    setStep('detect-platform');
  }

  function handleBranch(b: InstallBranch) {
    setBranch(b);
    setStep(b === 'wp' ? 'install-wp' : 'install-non-wp');
  }

  function handleVerified() {
    setStep('done');
    if (domain) onProceed(domain.id);
  }

  // Step indicator
  const STEPS: WizardStep[] = ['add-domain', 'detect-platform', branch === 'wp' ? 'install-wp' : 'install-non-wp', 'verify'];
  const currentIdx = STEPS.indexOf(step);

  return (
    <div className="max-w-md mx-auto py-12 px-4">
      {/* Progress dots */}
      {step !== 'done' && (
        <div className="flex items-center gap-2 mb-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= currentIdx - 1 ? 'bg-brand-green' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        {step === 'add-domain' && (
          <AddDomainStep onNext={handleDomainCreated} />
        )}
        {step === 'detect-platform' && domain && (
          <DetectPlatformStep domain={domain} onBranch={handleBranch} />
        )}
        {step === 'install-wp' && domain && (
          <WpInstallStep domain={domain} onVerified={handleVerified} />
        )}
        {step === 'install-non-wp' && domain && (
          <NonWpInstallStep domain={domain} onVerified={handleVerified} />
        )}
        {step === 'done' && (
          <div className="text-center space-y-3 py-4">
            <div className="text-4xl">✓</div>
            <h2 className="text-lg font-semibold text-brand-ink">You're connected!</h2>
            <p className="text-sm text-gray-600">Taking you to the next step…</p>
          </div>
        )}
      </div>
    </div>
  );
}
